/**
 * Example Library Component
 *
 * Main component for browsing and selecting EnergyPlus example files.
 */

import { useState, useCallback, useMemo } from 'react';
import type {
  ExampleFileMetadata,
  ExampleSearchQuery,
  ExampleCategory,
  ComplexityLevel,
  HVACSystemType,
  ExampleLibraryViewMode,
} from '../types';
import {
  searchExamplesWithFacets,
  getInitialFacets,
  getExampleById,
  getLearningPath,
} from '../services/exampleSearch';
import { ExampleCard } from './ExampleCard';
import { ExampleFilters } from './ExampleFilters';
import { ExamplePreview } from './ExamplePreview';
import { ResizablePanel } from '@/components/ResizablePanel';
import { LEARNING_CONCEPTS } from '../types';

export interface ExampleLibraryProps {
  onSelect?: (example: ExampleFileMetadata) => void;
  onClose?: () => void;
}

export function ExampleLibrary({ onSelect, onClose }: ExampleLibraryProps) {
  // View mode state
  const [viewMode, setViewMode] = useState<ExampleLibraryViewMode>('list');

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<ExampleCategory[]>([]);
  const [selectedComplexity, setSelectedComplexity] = useState<ComplexityLevel[]>([]);
  const [selectedHVACSystems, setSelectedHVACSystems] = useState<HVACSystemType[]>([]);

  // Preview state
  const [previewExample, setPreviewExample] = useState<ExampleFileMetadata | null>(null);

  // Selected learning path
  const [selectedLearningPath, setSelectedLearningPath] = useState<string | null>(null);

  // Build search query
  const searchQuery = useMemo<ExampleSearchQuery>(() => {
    const query: ExampleSearchQuery = {};

    if (searchText.trim()) {
      query.text = searchText;
    }

    if (selectedCategories.length > 0) {
      query.category = selectedCategories;
    }

    if (selectedComplexity.length > 0) {
      query.complexity = selectedComplexity;
    }

    if (selectedHVACSystems.length > 0) {
      query.hvacSystem = selectedHVACSystems;
    }

    return query;
  }, [searchText, selectedCategories, selectedComplexity, selectedHVACSystems]);

  // Execute search
  const searchResults = useMemo(() => {
    return searchExamplesWithFacets(searchQuery);
  }, [searchQuery]);

  // Get learning path examples
  const learningPathExamples = useMemo(() => {
    if (!selectedLearningPath) return null;
    return getLearningPath(selectedLearningPath as any);
  }, [selectedLearningPath]);

  // Handle file selection
  const handleSelect = useCallback(
    (example: ExampleFileMetadata) => {
      onSelect?.(example);
    },
    [onSelect]
  );

  // Handle preview
  const handlePreview = useCallback((example: ExampleFileMetadata) => {
    setPreviewExample(example);
  }, []);

  // Handle navigation to related example
  const handleNavigateToExample = useCallback((exampleId: string) => {
    const example = getExampleById(exampleId);
    if (example) {
      setPreviewExample(example);
    }
  }, []);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setSelectedCategories([]);
    setSelectedComplexity([]);
    setSelectedHVACSystems([]);
    setSelectedLearningPath(null);
  }, []);

  const hasActiveFilters =
    searchText.trim() !== '' ||
    selectedCategories.length > 0 ||
    selectedComplexity.length > 0 ||
    selectedHVACSystems.length > 0;

  const activeFilterCount =
    selectedCategories.length + selectedComplexity.length + selectedHVACSystems.length;

  // Initial facets for when no filters are applied
  const initialFacets = useMemo(() => getInitialFacets(), []);

  // Use dynamic facets from search results
  const currentFacets = searchResults.facets;

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h2 className="text-lg font-semibold">EnergyPlus Example Library</h2>
          <p className="text-sm text-muted-foreground">
            Learn from {searchResults.totalCount} curated example files
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-2 hover:bg-muted"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search bar and view toggle */}
      <div className="border-b px-4 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          {/* Search input */}
          <div className="relative flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search examples, objects, tags..."
              className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Filter toggle button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm ${
                hasActiveFilters ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted'
              }`}
              aria-label="Show filters"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* View mode toggle */}
            <div className="flex rounded-md border">
              <button
                onClick={() => {
                  setViewMode('list');
                  setSelectedLearningPath(null);
                }}
                className={`px-3 py-1.5 text-sm ${
                  viewMode === 'list' && !selectedLearningPath
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                aria-label="List view"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
              <button
                onClick={() => {
                  setViewMode('grid');
                  setSelectedLearningPath(null);
                }}
                className={`px-3 py-1.5 text-sm ${
                  viewMode === 'grid' && !selectedLearningPath
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                aria-label="Grid view"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('learning-path')}
                className={`px-3 py-1.5 text-sm ${
                  viewMode === 'learning-path' || selectedLearningPath
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
                aria-label="Learning path view"
                title="Learning Paths"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                  <path d="M6 12v5c3 3 9 3 12 0v-5" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Filters sidebar (desktop) */}
        {showFilters && (
          <ResizablePanel
            side="left"
            defaultWidth={256}
            minWidth={180}
            maxWidth={400}
            storageKey="example-library-filters-panel"
            className="overflow-y-auto p-4"
            hideOnMobile={true}
            mobileBreakpoint="md"
            resizeAriaLabel="Resize filters panel"
          >
            <ExampleFilters
              facets={currentFacets}
              selectedCategories={selectedCategories}
              selectedComplexity={selectedComplexity}
              selectedHVACSystems={selectedHVACSystems}
              onCategoriesChange={setSelectedCategories}
              onComplexityChange={setSelectedComplexity}
              onHVACSystemsChange={setSelectedHVACSystems}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </ResizablePanel>
        )}

        {/* Results area */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {/* Learning Path View */}
          {(viewMode === 'learning-path' || selectedLearningPath) && !learningPathExamples && (
            <LearningPathSelector
              facets={initialFacets}
              onSelectPath={setSelectedLearningPath}
            />
          )}

          {/* Learning Path Results */}
          {selectedLearningPath && learningPathExamples && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">
                    Learning Path: {LEARNING_CONCEPTS[selectedLearningPath as keyof typeof LEARNING_CONCEPTS]?.name ?? selectedLearningPath}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {learningPathExamples.length} examples, ordered by difficulty
                  </p>
                </div>
                <button
                  onClick={() => setSelectedLearningPath(null)}
                  className="rounded-md border px-3 py-1 text-sm hover:bg-muted"
                >
                  Back to Paths
                </button>
              </div>
              <div className="space-y-3">
                {learningPathExamples.map((example, index) => (
                  <div key={example.id} className="flex items-start gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <ExampleCard
                        example={example}
                        onSelect={() => handleSelect(example)}
                        onPreview={() => handlePreview(example)}
                        viewMode="list"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Default List/Grid View */}
          {viewMode !== 'learning-path' && !selectedLearningPath && (
            <>
              {/* No filters - show welcome */}
              {!hasActiveFilters && searchResults.results.length > 0 && (
                <div className="mb-6">
                  <h3 className="mb-3 font-semibold">Start Learning</h3>
                  <div className="grid gap-3 md:grid-cols-3">
                    <QuickStartCard
                      title="Beginner Examples"
                      description="Start with simple models to learn the basics"
                      icon="book"
                      onClick={() => setSelectedComplexity(['beginner'])}
                    />
                    <QuickStartCard
                      title="HVAC Systems"
                      description="Explore different HVAC system types"
                      icon="wind"
                      onClick={() => setSelectedCategories(['hvac-systems'])}
                    />
                    <QuickStartCard
                      title="Learning Paths"
                      description="Follow guided learning sequences"
                      icon="path"
                      onClick={() => setViewMode('learning-path')}
                    />
                  </div>
                </div>
              )}

              {/* Results count */}
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {searchResults.totalCount} example{searchResults.totalCount !== 1 ? 's' : ''} found
                </p>
              </div>

              {/* Results grid/list */}
              {viewMode === 'grid' ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {searchResults.results.map((example) => (
                    <ExampleCard
                      key={example.id}
                      example={example}
                      onSelect={() => handleSelect(example)}
                      onPreview={() => handlePreview(example)}
                      viewMode="grid"
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.results.map((example) => (
                    <ExampleCard
                      key={example.id}
                      example={example}
                      onSelect={() => handleSelect(example)}
                      onPreview={() => handlePreview(example)}
                      viewMode="list"
                    />
                  ))}
                </div>
              )}

              {/* Empty state */}
              {searchResults.results.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mb-4 text-muted-foreground/50"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <h3 className="mb-2 text-lg font-medium">No examples found</h3>
                  <p className="mb-4 max-w-sm text-sm text-muted-foreground">
                    Try adjusting your filters or search terms.
                  </p>
                  <button
                    onClick={handleClearFilters}
                    className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile filters drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowFilters(false)}
          />
          <div className="relative ml-auto h-full w-80 max-w-[85vw] overflow-y-auto bg-background shadow-xl">
            <div className="sticky top-0 flex items-center justify-between border-b bg-background px-4 py-3">
              <h3 className="font-semibold">Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="rounded-md p-1.5 hover:bg-muted"
                aria-label="Close filters"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <ExampleFilters
                facets={currentFacets}
                selectedCategories={selectedCategories}
                selectedComplexity={selectedComplexity}
                selectedHVACSystems={selectedHVACSystems}
                onCategoriesChange={setSelectedCategories}
                onComplexityChange={setSelectedComplexity}
                onHVACSystemsChange={setSelectedHVACSystems}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
                pendingMode
                onApply={() => setShowFilters(false)}
                onCancel={() => setShowFilters(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewExample && (
        <ExamplePreview
          example={previewExample}
          onClose={() => setPreviewExample(null)}
          onSelect={() => {
            handleSelect(previewExample);
            setPreviewExample(null);
          }}
          onNavigate={handleNavigateToExample}
        />
      )}
    </div>
  );
}

function QuickStartCard({
  title,
  description,
  icon,
  onClick,
}: {
  title: string;
  description: string;
  icon: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-muted/50"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon === 'book' && (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
        )}
        {icon === 'wind' && (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
            <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
            <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
          </svg>
        )}
        {icon === 'path' && (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
            <path d="M6 12v5c3 3 9 3 12 0v-5" />
          </svg>
        )}
      </div>
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}

function LearningPathSelector({
  facets,
  onSelectPath,
}: {
  facets: any;
  onSelectPath: (path: string) => void;
}) {
  // Group concepts by relevance
  const popularPaths = facets.concepts.slice(0, 12);

  return (
    <div>
      <div className="mb-6 text-center">
        <h3 className="text-xl font-semibold">Learning Paths</h3>
        <p className="mt-1 text-muted-foreground">
          Follow guided sequences to master specific topics
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {popularPaths.map(({ concept, count }: { concept: string; count: number }) => {
          const info = LEARNING_CONCEPTS[concept as keyof typeof LEARNING_CONCEPTS];
          if (!info) return null;

          return (
            <button
              key={concept}
              onClick={() => onSelectPath(concept)}
              className="flex flex-col items-start rounded-lg border p-4 text-left transition-colors hover:border-primary/50 hover:bg-muted/50"
            >
              <h4 className="font-medium">{info.name}</h4>
              <p className="mt-1 text-sm text-muted-foreground">{info.description}</p>
              <span className="mt-2 text-xs text-primary">{count} examples</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
